import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Divider,
  Box,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon, Business as BusinessIcon } from "@mui/icons-material";
import { db } from "../../../firebase-config";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { Link } from "react-router-dom";

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companiesRef = collection(db, "companies");
        const snapshot = await getDocs(companiesRef);
        const companyList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCompanies(companyList);
      } catch (err) {
        console.error("Error fetching companies:", err);
        setError("Failed to load companies.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  const handleApproveCompany = async (companyId) => {
    try {
      const companyRef = doc(db, "companies", companyId);
      await updateDoc(companyRef, { approved: true });
      setCompanies((prev) =>
        prev.map((company) => (company.id === companyId ? { ...company, approved: true } : company))
      );
    } catch (err) {
      console.error("Error approving company:", err);
      setError("Failed to approve company.");
    }
  };

  const handleRejectCompany = async (companyId) => {
    try {
      await deleteDoc(doc(db, "companies", companyId));
      setCompanies((prev) => prev.filter((company) => company.id !== companyId));
    } catch (err) {
      console.error("Error rejecting company:", err);
      setError("Failed to reject company.");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Company Management
      </Typography>

      {companies.length === 0 ? (
        <Typography variant="body1">No companies found.</Typography>
      ) : (
        <List>
          {companies.map((company, index) => (
            <React.Fragment key={company.id}>
              <ListItem component={Link} to={`/admin/companies/${company.id}`} alignItems="flex-start">
                <BusinessIcon sx={{ marginRight: 2 }} />
                <ListItemText
                  primary={company.name}
                  secondary={company.approved ? `Approved (ID: ${company.id})` : "Pending Approval"}
                />
                <ListItemSecondaryAction>
                  {!company.approved ? (
                    <>
                      <IconButton color="success" onClick={() => handleApproveCompany(company.id)}>
                        <CheckIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleRejectCompany(company.id)}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Approved
                    </Typography>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
              {index !== companies.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Container>
  );
};

export default AdminCompanies;
